// useSlashMenu — the editor's "/" menu state (split out of EditorHost.tsx to
// keep it under the file-size ceiling): the slash trigger of AT MOST one
// prose block (the one being typed into) with the query typed after the
// leading "/", the keyboard cursor into the filtered rows, and the menu they
// resolve to (the one Create menu, in its groups' order). A pick clears the
// trigger and hands the row, the block it was typed in, the words typed and
// how it was picked (Enter or the pointer) to `commit`.
// While no menu is open, the surface's own state at a block shows instead:
// the line that asks (`asking`, the Studio's Ask here), else the anchor of a
// step a surface row opened (`heldAt`).
// Words after "/" keep the menu open (the Studio's Ask here takes them);
// where no row takes the words, there is no menu, and Enter is plain Enter.

export type { SlashPick };
export { useSlashMenu };

import { useState } from "react";
import type { SlashTriggerState } from "../Editor/inputRules.js";
import type {
    SlashMenuState,
    SlashTrigger,
} from "../Editor/MaterialDocument/MaterialDocument.js";
import {
    inMenuOrder,
    type SlashItem,
    slashAsks,
    slashRowsFor,
    slashStartAt,
} from "../Editor/SlashMenu/slashItems.js";
import type { ElementId } from "../Editor/types.js";
import type { SurfaceSlash } from "./types.js";

// How a row was picked: Enter (the only way that sends a request), or the
// pointer.
type SlashPick = "enter" | "pointer";

type Tracked = SlashTrigger & { readonly query: string };

type SlashMenu = {
    // The tracked block's trigger (null: no block is tracked; every prose
    // block's EditableText reads "closed").
    readonly slashTrigger: Tracked | null;
    readonly onSlashTrigger: (
        id: ElementId,
        state: SlashTriggerState,
        query: string,
    ) => void;
    // The menu, once the trigger is "open", or the surface's own state at a
    // block (undefined: none).
    readonly slashMenuState: SlashMenuState | undefined;
    readonly onSlashNavigate: (direction: "up" | "down") => void;
    // Enter on the open menu: whether it picked a row.
    readonly onSlashCommitActive: () => boolean;
};

const useSlashMenu = (
    // Every row, before the typed query narrows them.
    items: readonly SlashItem[],
    commit: (
        item: SlashItem,
        anchor: ElementId,
        words: string,
        how: SlashPick,
    ) => void,
    // The surface around the editor (absent: none).
    surface: SurfaceSlash | undefined,
    // Words left in the line that asked become the block's text.
    keepText: (id: ElementId, text: string) => void,
): SlashMenu => {
    const [tracked, setTracked] = useState<Tracked | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const rowsFor = (query: string) => slashRowsFor(inMenuOrder(items), query);
    const query = tracked?.query ?? "";
    const shown = rowsFor(query);
    // Words no row takes: no menu (the trigger reads closed).
    const untaken = shown.length === 0 && /\s/.test(query.trim());
    const slashTrigger: Tracked | null =
        tracked !== null && tracked.state === "open" && untaken
            ? {
                  forElementId: tracked.forElementId,
                  state: "closed",
                  query,
              }
            : tracked;

    // "closed" clears the tracked block; any other state replaces it. The
    // cursor starts where the words point (slashStartAt).
    const onSlashTrigger = (
        id: ElementId,
        state: SlashTriggerState,
        next: string,
    ) => {
        setTracked(
            state === "closed"
                ? null
                : { forElementId: id, state, query: next },
        );
        if (state === "open") setActiveIndex(slashStartAt(rowsFor(next), next));
    };

    // The block learns its "/query" was a command (`picked`: it drops the
    // text), whether the pick came by Enter or by the pointer.
    const onSlashCommit = (item: SlashItem, how: SlashPick) => {
        if (tracked === null) return;
        setTracked({
            forElementId: tracked.forElementId,
            state: "picked",
            query: "",
        });
        commit(item, tracked.forElementId, tracked.query, how);
    };

    const active = shown[activeIndex];
    const asking = surface?.asking;
    const heldAt = surface?.heldAt;
    const slashMenuState: SlashMenuState | undefined =
        slashTrigger !== null && slashTrigger.state === "open"
            ? {
                  type: "open",
                  forElementId: slashTrigger.forElementId,
                  items: shown,
                  activeIndex,
                  onCommit: (item) => onSlashCommit(item, "pointer"),
                  onHoverIndex: setActiveIndex,
                  // Offline is the face's to say (EditableMaterial).
                  disabled: false,
                  asking:
                      active !== undefined &&
                      slashAsks(active) &&
                      query.trim() !== "",
              }
            : surface !== undefined && asking !== undefined
              ? {
                    // The line that asks: Enter sends; any other way out
                    // keeps what was written as the block's text (Undo
                    // removes it).
                    type: "asking",
                    forElementId: asking.at,
                    words: asking.words,
                    // Offline is the face's to say (EditableMaterial).
                    online: true,
                    max: surface.askMax,
                    onAsk: surface.onAsk,
                    onLeave: (words) => {
                        if (words.trim() !== "") keepText(asking.at, words);
                        surface.onLeaveAsk();
                    },
                }
              : heldAt === undefined
                ? undefined
                : { type: "held", forElementId: heldAt };

    // ↑/↓ wrap around the filtered rows; Enter picks the active one (the
    // same pick a click makes). Both no-op while closed or empty.
    const onSlashNavigate = (direction: "up" | "down") => {
        const count = slashMenuState?.type === "open" ? shown.length : 0;
        if (count === 0) return;
        setActiveIndex((i) => {
            const next = direction === "down" ? i + 1 : i - 1;
            return ((next % count) + count) % count;
        });
    };
    const onSlashCommitActive = (): boolean => {
        if (slashMenuState?.type !== "open" || active === undefined)
            return false;
        onSlashCommit(active, "enter");
        return true;
    };

    return {
        slashTrigger,
        onSlashTrigger,
        slashMenuState,
        onSlashNavigate,
        onSlashCommitActive,
    };
};
